import React, { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import HintMessage from 'components/HintMessage';
import { AGREEMENT_LIMITS } from 'models/Agreement';
import { getHasEmptyCustomLabel, getHasSignatureField } from 'agreementUtils';
import useMatchMedia from 'hooks/useMatchMedia';
import FieldEditorContext from '../../providers/FieldEditor/FieldEditor.context';
import {
  FIELD_PALETTE_GROUPS,
  FIELD_TYPE_DRAG_FORMAT,
  FieldTypeConfig,
  getFieldTypeConfigsInGroup,
  getIsFieldLimitReached,
} from '../../utils';

import './FieldPalette.styles.scss';
import './FieldPalette.styles.responsive.scss';

type FieldPaletteProps = {
  className?: string;
  isDisabled?: boolean;
};

const FieldPalette: React.FC<FieldPaletteProps> = (props) => {
  const { className, isDisabled } = props;

  const { fields, placingConfig, setPlacingConfig, setSelectedFieldId } =
    useContext(FieldEditorContext);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const isFieldLimitReached = getIsFieldLimitReached(fields);

  const classes = classNames('field-palette', className);

  const onTypeClick = (config: FieldTypeConfig) => {
    setSelectedFieldId(undefined);
    setPlacingConfig(placingConfig?.type === config.type ? undefined : config);
  };

  const onTypeDragStart =
    (config: FieldTypeConfig) =>
    (event: React.DragEvent<HTMLButtonElement>) => {
      event.dataTransfer.setData(FIELD_TYPE_DRAG_FORMAT, config.type);
      event.dataTransfer.effectAllowed = 'copy';
      setSelectedFieldId(undefined);
      setPlacingConfig(config);
    };

  const onTypeDragEnd = (event: React.DragEvent<HTMLButtonElement>) => {
    if (event.dataTransfer.dropEffect === 'none') setPlacingConfig(undefined);
  };

  return (
    <div className={classes}>
      <p className="field-palette__title">{t('agreementFields')}</p>

      {!isDisabled && (
        <p className="field-palette__hint">
          {placingConfig
            ? t('agreementPlaceFieldHint', { field: t(placingConfig.labelKey) })
            : t(
                isPhablet ? 'agreementPickFieldHint' : 'agreementDragFieldHint',
              )}
        </p>
      )}

      {isFieldLimitReached && (
        <HintMessage
          type="info"
          icon={null}
          message={t('agreementFieldLimitReached', {
            max: AGREEMENT_LIMITS.MAX_FIELDS,
          })}
        />
      )}

      {!isDisabled && !getHasSignatureField(fields) && (
        <HintMessage
          type="info"
          icon={null}
          message={t('agreementAddSignatureHint')}
        />
      )}

      {getHasEmptyCustomLabel(fields) && (
        <HintMessage
          type="info"
          icon={null}
          message={t('agreementAddFieldLabelHint')}
        />
      )}

      {FIELD_PALETTE_GROUPS.map(({ group, titleKey }) => (
        <div key={group} className="field-palette__group">
          {titleKey && (
            <p className="field-palette__group-title">{t(titleKey)}</p>
          )}

          <div className="field-palette__types">
            {getFieldTypeConfigsInGroup(group).map((config) => (
              <Button
                key={config.type}
                type="button"
                variant="outline"
                icon={config.Icon}
                ripple={false}
                draggable={!isDisabled && !isFieldLimitReached && !isPhablet}
                disabled={isDisabled || isFieldLimitReached}
                className={classNames(
                  'field-palette__type',
                  `field-palette__type--${group}`,
                  {
                    'field-palette__type--active':
                      placingConfig?.type === config.type,
                  },
                )}
                onClick={() => onTypeClick(config)}
                onDragStart={onTypeDragStart(config)}
                onDragEnd={onTypeDragEnd}
              >
                {t(config.labelKey)}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FieldPalette;
